import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import api from '../../../utils/api'
import { toast } from 'react-toastify';
import AdminLayout from '../components/AdminLayout';
import { useSortableData, SortDirection } from '../components/SortableTable';

interface AdminUser {
  id: number;
  name: string;
  email: string;
  avatarUrl?: string;
  editCapabilities: boolean;
  createTransactions: boolean;
  canImpersonate?: boolean;
}

interface AutocompleteUser {
  id: number;
  name: string;
  email: string;
  avatarUrl?: string;
}

const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  margin-bottom: 16px;
  color: #6b7280;
  a { color: #00a6c2; text-decoration: none; &:hover { text-decoration: underline; } }
`;

const PageHeader = styled.div`
  margin-bottom: 24px;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #00a6c2;
  margin: 0;
`;

const Subtitle = styled.div`
  font-size: 14px;
  color: #6b7280;
  margin-top: 4px;
`;

const SearchSection = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
`;

const SearchButton = styled.button`
  padding: 10px 24px;
  background: #00a6c2;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  &:hover { background: #0094ab; }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
`;

const Th = styled.th<{ $sortable?: boolean; $sorted?: boolean }>`
  text-align: left;
  padding: 12px;
  font-size: 13px;
  font-weight: 600;
  color: ${({ $sorted }) => ($sorted ? '#00a6c2' : '#374151')};
  border-bottom: 2px solid #e5e7eb;
  background: #f9fafb;
  cursor: ${({ $sortable }) => ($sortable ? 'pointer' : 'default')};
  user-select: none;
  white-space: nowrap;
  
  &:hover {
    ${({ $sortable }) => $sortable && `
      background: #f0f0f0;
      color: #00a6c2;
    `}
  }
`;

const SortIndicator = styled.span<{ $direction: SortDirection }>`
  margin-left: 4px;
  
  &::after {
    content: '${({ $direction }) => {
      if ($direction === 'asc') return '↓';
      if ($direction === 'desc') return '↑';
      return '';
    }}';
  }
`;

const Td = styled.td`
  padding: 12px;
  font-size: 14px;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
  vertical-align: middle;
`;

const UserCell = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Avatar = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
`;

const PlaceholderAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 14px;
`;

const UserInfo = styled.div`
  a { color: #00a6c2; text-decoration: none; font-weight: 500; &:hover { text-decoration: underline; } }
  small { display: block; color: #6b7280; font-size: 12px; }
`;

const ActionButton = styled.button<{ $disabled?: boolean }>`
  padding: 6px 10px;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  margin-right: 4px;
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  filter: drop-shadow(0 0 0.5px rgba(0, 0, 0, 0.3));
  &:hover { background: ${({ $disabled }) => ($disabled ? '#f3f4f6' : '#e5e7eb')}; }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 4px;
  margin-top: 24px;
`;

const PageButton = styled.button<{ $active?: boolean }>`
  padding: 6px 12px;
  border: 1px solid ${({ $active }) => ($active ? '#00a6c2' : '#d1d5db')};
  background: ${({ $active }) => ($active ? '#00a6c2' : 'white')};
  color: ${({ $active }) => ($active ? 'white' : '#374151')};
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  &:hover { background: ${({ $active }) => ($active ? '#0094ab' : '#f3f4f6')}; }
  &:disabled { cursor: not-allowed; opacity: 0.5; }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 48px;
  color: #6b7280;
`;

// Autocomplete Styled Components
const AutocompleteWrapper = styled.div`
  position: relative;
  flex: 1;
  max-width: 300px;
`;

const AutocompleteInput = styled.input`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
  background: white;
  box-sizing: border-box;
  &:focus { outline: none; border-color: #00a6c2; }
`;

const AutocompleteDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #d1d5db;
  border-top: none;
  border-radius: 0 0 4px 4px;
  max-height: 350px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const AutocompleteItem = styled.div<{ $isHighlighted?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  cursor: pointer;
  background: ${props => (props.$isHighlighted ? '#f3f4f6' : 'white')};

  &:hover {
    background: #f3f4f6;
  }
`;

const AutocompleteAvatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
`;

const AutocompletePlaceholderAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 12px;
`;

const AutocompleteUserInfo = styled.div`
  flex: 1;
`;

const AutocompleteUserName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #00a6c2;
`;

const AutocompleteUserEmail = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

function AdminAdminsPage() {
  const navigate = useNavigate();
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Sorting for admins table
  const { sortedData: sortedAdmins, requestSort, getSortDirection } = useSortableData(admins);

  // Autocomplete state
  const [searchQuery, setSearchQuery] = useState('');
  const [autocompleteResults, setAutocompleteResults] = useState<AutocompleteUser[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const fetchAdmins = useCallback(async (pageNum: number) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pageNum.toString());

      const res = await api.get(`/admin2/users/admins.json?${params.toString()}`);
      setAdmins(res.data.admins || []);
      setTotalCount(res.data.totalCount || 0);
      setTotalPages(res.data.totalPages || 1);
    } catch (err) {
      toast.error('Failed to load admins');
      setAdmins([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAdmins(page);
  }, [page, fetchAdmins]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // User autocomplete search
  const searchUsers = useCallback(async (query: string) => {
    if (query.length < 2) {
      setAutocompleteResults([]);
      return;
    }

    try {
      const response = await api.get('/admin2/users/autocomplete.json', { params: { query } });
      setAutocompleteResults(response.data || []);
      setHighlightedIndex(-1);
    } catch (error) {
      console.error('User autocomplete search failed:', error);
      setAutocompleteResults([]);
    }
  }, []);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setShowDropdown(true);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchUsers(value);
    }, 300);
  };

  const handleSelectUser = (user: AutocompleteUser) => {
    // Navigate to the user's edit page
    navigate(`/admin2/users/${user.id}/edit`);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (autocompleteResults.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev < autocompleteResults.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev > 0 ? prev - 1 : prev));
    } else if (e.key === 'Enter' && highlightedIndex >= 0) {
      e.preventDefault();
      handleSelectUser(autocompleteResults[highlightedIndex]);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  const handleSearch = () => {
    // The Search button can be used to trigger a search/filter if needed
    // For now it just closes the dropdown
    setShowDropdown(false);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleImpersonate = async (userId: number) => {
    try {
      // Follow the server-computed `redirectUrl` (= after_sign_in_path_for,
      // legacy parity → /tasks) instead of hardcoding '/'. See
      // AdminUserEditPage#handleImpersonate for the full rationale.
      const { data } = await api.post(`/admin2/users/${userId}/impersonate`);
      window.location.href = data?.redirectUrl || '/tasks';
    } catch (err) {
      console.error('Failed to impersonate user', err);
      toast.error('Failed to impersonate user');
    }
  };

  if (loading && admins.length === 0) {
    return (
      <AdminLayout title="Users" pageTitle="Admins">
        <LoadingState>Loading...</LoadingState>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Users" pageTitle="Admins">
      <Breadcrumb>
        <Link to="/admin2/users">Users</Link>
        <span>›</span>
        <span>Admins</span>
      </Breadcrumb>

      <PageHeader>
        <PageTitle>Admins</PageTitle>
        <Subtitle>{totalCount} users</Subtitle>
      </PageHeader>

      <SearchSection>
        <AutocompleteWrapper ref={dropdownRef}>
          <AutocompleteInput
            type="text"
            placeholder="Start typing to find a user"
            value={searchQuery}
            onChange={e => handleSearchChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => { if (autocompleteResults.length > 0) setShowDropdown(true); }}
          />
          {showDropdown && autocompleteResults.length > 0 && (
            <AutocompleteDropdown>
              {autocompleteResults.map((user, index) => (
                <AutocompleteItem
                  key={user.id}
                  $isHighlighted={index === highlightedIndex}
                  onClick={() => handleSelectUser(user)}
                >
                  {user.avatarUrl ? (
                    <AutocompleteAvatar src={user.avatarUrl} alt={user.name} />
                  ) : (
                    <AutocompletePlaceholderAvatar>👤</AutocompletePlaceholderAvatar>
                  )}
                  <AutocompleteUserInfo>
                    <AutocompleteUserName>{user.name}</AutocompleteUserName>
                    <AutocompleteUserEmail>{user.email}</AutocompleteUserEmail>
                  </AutocompleteUserInfo>
                </AutocompleteItem>
              ))}
            </AutocompleteDropdown>
          )}
        </AutocompleteWrapper>
        <SearchButton onClick={handleSearch}>Search</SearchButton>
      </SearchSection>

      <Table>
        <thead>
          <tr>
            <Th $sortable $sorted={getSortDirection('name') !== null} onClick={() => requestSort('name')}>
              Name <SortIndicator $direction={getSortDirection('name')} />
            </Th>
            <Th $sortable $sorted={getSortDirection('editCapabilities') !== null} onClick={() => requestSort('editCapabilities')}>
              Edit capabilities? <SortIndicator $direction={getSortDirection('editCapabilities')} />
            </Th>
            <Th $sortable $sorted={getSortDirection('createTransactions') !== null} onClick={() => requestSort('createTransactions')}>
              Create transactions? <SortIndicator $direction={getSortDirection('createTransactions')} />
            </Th>
            <Th>Actions</Th>
          </tr>
        </thead>
        <tbody>
          {sortedAdmins.map((admin) => (
            <tr key={admin.id}>
              <Td>
                <UserCell>
                  {admin.avatarUrl ? (
                    <Avatar src={admin.avatarUrl} alt={admin.name} />
                  ) : (
                    <PlaceholderAvatar>👤</PlaceholderAvatar>
                  )}
                  <UserInfo>
                    <Link to={`/admin2/users/${admin.id}/edit`}>{admin.name}</Link>
                    <small>{admin.email}</small>
                  </UserInfo>
                </UserCell>
              </Td>
              <Td>{admin.editCapabilities ? 'true' : 'false'}</Td>
              <Td>{admin.createTransactions ? 'true' : 'false'}</Td>
              <Td>
                <ActionButton as={Link} to={`/admin2/users/${admin.id}/edit`} title="Edit">✏️</ActionButton>
                <ActionButton
                  title={admin.canImpersonate === false ? 'Cannot impersonate' : 'Impersonate'}
                  onClick={() => admin.canImpersonate !== false && handleImpersonate(admin.id)}
                  $disabled={admin.canImpersonate === false}
                >
                  👤
                </ActionButton>
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>

      {totalPages > 1 && (
        <Pagination>
          {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map((p) => (
            <PageButton
              key={p}
              $active={p === page}
              onClick={() => handlePageChange(p)}
            >
              {p}
            </PageButton>
          ))}
          {totalPages > 5 && <span>...</span>}
          <PageButton
            onClick={() => handlePageChange(page + 1)}
            disabled={page >= totalPages}
          >
            →
          </PageButton>
          <PageButton
            onClick={() => handlePageChange(totalPages)}
            disabled={page >= totalPages}
          >
            ⇥
          </PageButton>
        </Pagination>
      )}
    </AdminLayout>
  );
}

export default AdminAdminsPage;
